'use client';
/**
 * PRÉLÈVEMENT & RÉCEPTION — le cœur du dossier d'un examen, refondu (RQ, 14/09/2026).
 *
 * « Le dossier est devenu totalement incompréhensible. Il faut que les données
 *  soient plus structurées. On commence par le milieu qu'on peut prélever. On en
 *  rajoute ou en supprime et en cliquant sur un milieu on peut le paramétrer à
 *  part. Ensuite ce qu'on accepte à réception à partir de ce milieu et toutes
 *  les conditions qui incombent. Ensuite tout ce qui est commun. »
 *
 * ── Trois étages, dans cet ordre ────────────────────────────────────────────
 *  1. MILIEUX PRÉLEVÉS — `fiche.milieux` (liste fermée). Un clic règle le milieu.
 *  2. LE MILIEU CHOISI — préparation du patient (liste commune `lists.prep`,
 *     plusieurs choix), puis les CONTENANTS acceptés à réception qui en viennent
 *     (`materiels[]` dont `milieu` vaut ce code) : contenant, transport (mode,
 *     délai, consigne), conservation, volume, consigne propre, qui le voit.
 *  3. COMMUN — renseignements cliniques, délai d'ajout d'analyse.
 *
 * ── Vocabulaire (RQ, 14/09) ─────────────────────────────────────────────────
 * On parle de CONTENANTS à l'écran (« matière » perturbait). Le nom technique
 * `materiels` ne change pas.
 *
 * ── Ce qui n'est pas publié se VOIT ─────────────────────────────────────────
 * L'écran compare la copie de travail (brouillon compris) à la fiche PUBLIÉE :
 * bandeau récapitulatif, pastille « non publié » sur chaque milieu et chaque
 * contenant touchés, milieux retirés barrés. Écriture au BROUILLON ; la
 * publication se fait dans Référentiel › Circuit (proposer → valider → diffuser).
 *
 * Contenant et transport sont portés PAR L'ENTRÉE depuis le 14/09/2026 (recopiés
 * du profil) : le profil n'est plus que le modèle proposé à l'ajout.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { inputStyle, Skeleton, useConfirmation, SelecteurPastilles } from '@/components/ui';
import { moteur, collection } from './Registres';
import { cheminDe } from '@/lib/adresse';
import { LECTEURS_CATALOGUE, lecteursEntree, lecteursParDefautEntree, type LecteurCatalogue } from '@/shared/lectureCatalogue';
import type { MaterielFiche } from './MaterielsFiche';

import { Bouton } from '@/shared/Bouton';
type Profil = {
  code: string; libelle: string; matiere: string; etat: string; recipient: string | null;
  transportMode: string; transportDelaiMax: string | null; transportConsigne: string | null;
  criteresReception: { libelle: string; requis: boolean }[]; attestations: string[]; refuses: string[];
  resteAFaire: string[]; donneesReception: string[];
};
type Entree = MaterielFiche & { recipient?: string | null; transportMode?: string; transportDelaiMax?: string | null; transportConsigne?: string | null };
type Code = { code: string; libelle: string; aide?: string };

/** Les matières qu'un milieu fournit — pour proposer d'abord les bons contenants. */
export const MATIERES_DU_MILIEU: Record<string, string[]> = {
  sang_veineux: ['Sérum', 'Plasma', 'Plasma pauvre en plaquettes', 'Sang total'],
  sang_arteriel: ['Sang total', 'Plasma'],
  sang_capillaire: ['Sang total', 'Sérum', 'Plasma'],
  urine: ['Urine'], selles: ['Selles'], sperme: ['Sperme'], expectoration: ['Expectoration'],
  lcr: ['LCR'], ponction: ['Liquide de ponction'],
  prelevement_genital: ['Écouvillon'], prelevement_orl: ['Écouvillon'], prelevement_cutane: ['Écouvillon'],
  biopsie: ['Tissu'],
};
const ETAT: Record<string, string> = {
  tube_primaire: 'tube primaire', matiere_separee: 'séparé', matiere_congelee: 'congelé',
  isolat: 'isolat', adn_extrait: 'ADN extrait', tissu: 'tissu',
};
const MODES: { code: string; libelle: string; cle: 'ambiante' | 'refrigere' | 'congele'; couleur: string; duree: string }[] = [
  { code: 'ambiant', libelle: 'Température ambiante', cle: 'ambiante', couleur: 'var(--amber-600)', duree: 'Conservation à température ambiante' },
  { code: 'refrigere', libelle: 'Réfrigéré 2–8 °C', cle: 'refrigere', couleur: 'var(--blue-600)', duree: 'Conservation à 2–8 °C' },
  { code: 'congele', libelle: 'Congelé', cle: 'congele', couleur: 'var(--violet-600)', duree: 'Conservation congelée' },
];
const A_CLASSER = '__a_classer__';
const SEP = ' · ';

const titreEtage: React.CSSProperties = { display: 'flex', alignItems: 'baseline', gap: 10, fontSize: 13, fontWeight: 800, color: 'var(--text-strong)', margin: '0 0 8px', flexWrap: 'wrap' };
const numero: React.CSSProperties = { display: 'inline-grid', placeItems: 'center', width: 22, height: 22, borderRadius: '50%', background: 'var(--color-primary)', color: 'var(--text-on-primary)', fontSize: 12, fontWeight: 800, flex: 'none' };
const aide: React.CSSProperties = { fontSize: 12, color: 'var(--text-muted)', fontWeight: 400 };
const etiquette: React.CSSProperties = { display: 'grid', gap: 2, fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', minWidth: 0 };
const bloc: React.CSSProperties = { border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: 12, background: 'var(--surface-card)', minWidth: 0 };
const nonPublie: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', fontSize: 10.5, fontWeight: 800, letterSpacing: '.03em', borderRadius: 'var(--radius-pill)', padding: '1px 8px', background: 'var(--amber-50)', color: 'var(--amber-700)', border: '1px solid #EBD9B4', whiteSpace: 'nowrap' };
const sousTitre: React.CSSProperties = { fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' };

const tableau = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
const texte = (v: unknown) => (v === null || v === undefined ? '' : String(v));
const cleEntree = (m: Entree) => `${m.milieu || ''}|${m.profil}`;

export function PrelevementReception({ fiche, detail, estRq, onEcrit }: {
  fiche: { code: string };
  detail: Record<string, unknown>;
  estRq: boolean;
  onEcrit: () => void;
}) {
  const { demander, confirmation } = useConfirmation();
  const [profils, setProfils] = useState<Profil[] | null>(null);
  const [enums, setEnums] = useState<{ milieux: Code[]; recipient: Code[] }>({ milieux: [], recipient: [] });
  const [preps, setPreps] = useState<string[]>([]);
  const [brouillon, setBrouillon] = useState<Record<string, unknown> | null | undefined>(undefined);
  const [milieux, setMilieux] = useState<string[] | null>(null);
  const [materiels, setMateriels] = useState<Entree[]>([]);
  const [renseignements, setRenseignements] = useState('');
  const [delaiAjout, setDelaiAjout] = useState('');
  const [choisi, setChoisi] = useState<string | null>(null);
  const [ajoutMilieu, setAjoutMilieu] = useState('');
  const [ajoutContenant, setAjoutContenant] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [message, setMessage] = useState('');
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    let vivant = true;
    void collection('profil-materiels', ['code', 'libelle', 'matiere', 'etat', 'recipient', 'transportMode', 'transportDelaiMax', 'transportConsigne', 'criteresReception', 'attestations', 'refuses', 'resteAFaire', 'donneesReception'], 'ordre:asc')
      .then((rows) => { if (vivant) setProfils(rows as unknown as Profil[]); });
    void moteur('manuel-ref/parametrage').then((r) => {
      if (!vivant || !r.ok) return;
      const e = (r.data.enums as Record<string, Code[]> | undefined) || {};
      setEnums({ milieux: e.milieux || [], recipient: e.recipient || [] });
      const lists = ((r.data.parametrage as Record<string, unknown> | null)?.lists as Record<string, string[]> | undefined) || {};
      setPreps(lists.prep || []);
    });
    void moteur(`manuel-circuit/brouillon/${encodeURIComponent(fiche.code)}`).then((r) => {
      if (!vivant) return;
      setBrouillon(r.ok && r.data.existe ? ((r.data.values as Record<string, unknown>) || {}) : null);
    });
    return () => { vivant = false; };
  }, [fiche.code]);

  /* PUBLIÉ (ce que le catalogue affiche) et BASE (brouillon s'il existe, sinon publié). */
  const publie = useMemo(() => ({
    milieux: tableau<string>(detail.milieux), materiels: tableau<Entree>(detail.materiels),
    renseignements: texte(detail.renseignements), delaiAjout: texte(detail.delaiAjout),
  }), [detail]);
  const base = useMemo(() => {
    if (brouillon === undefined) return null;
    const lire = (k: string) => (brouillon && k in brouillon ? brouillon[k] : detail[k]);
    return {
      milieux: tableau<string>(lire('milieux')), materiels: tableau<Entree>(lire('materiels')),
      renseignements: texte(lire('renseignements')), delaiAjout: texte(lire('delaiAjout')),
    };
  }, [brouillon, detail]);

  if (base && milieux === null) {
    setMilieux(base.milieux);
    setMateriels(base.materiels);
    setRenseignements(base.renseignements);
    setDelaiAjout(base.delaiAjout);
    setChoisi(base.milieux[0] || (base.materiels.length ? A_CLASSER : null));
  }
  if (!base || milieux === null || !profils) return <Skeleton rows={4} />;

  const profil = (code: string) => profils.find((p) => p.code === code);
  const libelleMilieu = (code: string) => (code === A_CLASSER ? 'À classer' : enums.milieux.find((m) => m.code === code)?.libelle || code);
  const libelleContenant = (code: string | null | undefined) => (code ? enums.recipient.find((r) => r.code === code)?.libelle || code : '—');
  const duMilieu = (code: string) => materiels
    .map((m, i) => ({ m, i }))
    .filter(({ m }) => (code === A_CLASSER ? !m.milieu || !milieux.includes(m.milieu) : m.milieu === code));
  const aClasser = duMilieu(A_CLASSER);

  /* ── Écarts avec le PUBLIÉ ──────────────────────────────────────────────── */
  const publieParCle = new Map(publie.materiels.map((m) => [cleEntree(m), JSON.stringify(m)]));
  const courantParCle = new Map(materiels.map((m) => [cleEntree(m), JSON.stringify(m)]));
  const etatEntree = (m: Entree): 'ajoute' | 'modifie' | null => {
    const p = publieParCle.get(cleEntree(m));
    return p === undefined ? 'ajoute' : p !== JSON.stringify(m) ? 'modifie' : null;
  };
  const milieuxAjoutes = milieux.filter((c) => !publie.milieux.includes(c));
  const milieuxRetires = publie.milieux.filter((c) => !milieux.includes(c));
  const contenantsRetires = publie.materiels.filter((m) => !courantParCle.has(cleEntree(m)));
  const ecarts: string[] = [
    ...milieuxAjoutes.map((c) => `Milieu ajouté : ${libelleMilieu(c)}`),
    ...milieuxRetires.map((c) => `Milieu retiré : ${libelleMilieu(c)}`),
    ...materiels.filter((m) => etatEntree(m)).map((m) => {
      const p = profil(m.profil);
      return `${etatEntree(m) === 'ajoute' ? 'Contenant ajouté' : 'Contenant modifié'} : ${p ? `${p.matiere} — ${ETAT[p.etat] || p.etat}` : m.profil} (${libelleMilieu(m.milieu || A_CLASSER)})`;
    }),
    ...contenantsRetires.map((m) => { const p = profil(m.profil); return `Contenant retiré : ${p ? `${p.matiere} — ${ETAT[p.etat] || p.etat}` : m.profil}`; }),
    ...(renseignements !== publie.renseignements ? ['Renseignements cliniques modifiés'] : []),
    ...(delaiAjout !== publie.delaiAjout ? ['Délai d’ajout modifié'] : []),
  ];
  const modifieLocal = JSON.stringify([milieux, materiels, renseignements, delaiAjout])
    !== JSON.stringify([base.milieux, base.materiels, base.renseignements, base.delaiAjout]);

  const poser = (i: number, patch: Partial<Entree>) => {
    setMateriels(materiels.map((m, k) => (k === i ? { ...m, ...patch, source: 'declare' } : m)));
    setMessage('');
  };
  const ajouterMilieu = () => {
    if (!ajoutMilieu || milieux.includes(ajoutMilieu)) return;
    setMilieux([...milieux, ajoutMilieu]); setChoisi(ajoutMilieu); setAjoutMilieu(''); setMessage('');
  };
  const retirerMilieu = async (code: string) => {
    const lies = duMilieu(code).length;
    if (lies && !(await demander(`Retirer le milieu « ${libelleMilieu(code)} » ?\nSes ${lies} contenant(s) accepté(s) à réception seront retirés aussi.`, { confirmer: 'Retirer' }))) return;
    setMilieux(milieux.filter((m) => m !== code));
    setMateriels(materiels.filter((m) => m.milieu !== code));
    if (choisi === code) setChoisi(null);
    setMessage('');
  };
  /* Une entrée de contenant se construit à un seul endroit : « Ajouter » et
     « Enregistrer » passent par là, sans quoi l'une dériverait de l'autre. */
  const entreeContenant = (code: string, milieu: string | null): Entree | null => {
    const p = profil(code);
    if (!p || !milieu || milieu === A_CLASSER) return null;
    return {
      profil: p.code, accepte: true, source: 'declare', milieu, lecteurs: lecteursParDefautEntree(p.etat),
      recipient: p.recipient, transportMode: p.transportMode, transportDelaiMax: p.transportDelaiMax, transportConsigne: p.transportConsigne,
    };
  };
  const ajouter = () => {
    const e = entreeContenant(ajoutContenant, choisi);
    if (!e) return;
    setMateriels([...materiels, e]);
    setAjoutContenant(''); setMessage('');
  };

  const enregistrer = async () => {
    setEnvoi(true); setErreur(''); setMessage('');
    /* ⚠️ CE QUI EST CHOISI MAIS PAS « AJOUTÉ » PART AVEC LE RESTE (09/10/2026).
       Un milieu ou un contenant sélectionné dans sa liste sans cliquer
       « Ajouter » n'était dans aucun état que l'enregistrement lit : il
       disparaissait sans un mot, et le message disait « Enregistré ». */
    const milieuEnPlus = ajoutMilieu && !milieux.includes(ajoutMilieu) ? ajoutMilieu : '';
    const milieuxFinal = milieuEnPlus ? [...milieux, milieuEnPlus] : milieux;
    const contenantEnPlus = entreeContenant(ajoutContenant, milieuEnPlus || choisi);
    const materielsFinal = contenantEnPlus ? [...materiels, contenantEnPlus] : materiels;
    if (milieuEnPlus) { setMilieux(milieuxFinal); setChoisi(milieuEnPlus); setAjoutMilieu(''); }
    if (contenantEnPlus) { setMateriels(materielsFinal); setAjoutContenant(''); }
    const values: Record<string, unknown> = { ...(brouillon || {}) };
    if (JSON.stringify(milieuxFinal) !== JSON.stringify(base.milieux)) values.milieux = milieuxFinal;
    if (JSON.stringify(materielsFinal) !== JSON.stringify(base.materiels)) values.materiels = materielsFinal;
    if (renseignements !== base.renseignements) values.renseignements = renseignements;
    if (delaiAjout !== base.delaiAjout) values.delaiAjout = delaiAjout;
    const r = await moteur('manuel-circuit/brouillon', { code: fiche.code, values });
    setEnvoi(false);
    if (!r.ok) { setErreur(r.message); return; }
    setBrouillon(values);
    setMessage('Enregistré au brouillon.');
    onEcrit();
  };

  /* ── La carte d'un contenant accepté à réception ────────────────────────── */
  const carte = ({ m, i }: { m: Entree; i: number }) => {
    const p = profil(m.profil);
    const mode = MODES.find((x) => x.code === (m.transportMode || p?.transportMode));
    const lecteurs = lecteursEntree(m, p?.etat || '');
    const ecart = etatEntree(m);
    return (
      <div key={`${m.profil}-${i}`} style={{ ...bloc, borderLeft: `4px solid ${m.accepte ? (mode?.couleur || 'var(--border-default)') : 'var(--gray-300)'}`, display: 'grid', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 13.5, textDecoration: m.accepte ? 'none' : 'line-through' }}>
            {libelleContenant(m.recipient !== undefined ? m.recipient : p?.recipient)}
          </strong>
          {p && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{p.matiere} — {ETAT[p.etat] || p.etat}</span>}
          {ecart && <span style={nonPublie}>{ecart === 'ajoute' ? 'Ajouté · non publié' : 'Modifié · non publié'}</span>}
          <span style={{ flex: 1 }} />
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, fontWeight: 600 }}>
            <input type="checkbox" checked={m.accepte} onChange={() => poser(i, { accepte: !m.accepte })} /> Accepté
          </label>
          {choisi === A_CLASSER && (
            <select value="" aria-label="Rattacher à un milieu" onChange={(e) => poser(i, { milieu: e.currentTarget.value })} style={{ ...inputStyle, fontSize: 12, width: 'auto' }}>
              <option value="">Rattacher à…</option>
              {milieux.map((c) => <option key={c} value={c}>{libelleMilieu(c)}</option>)}
            </select>
          )}
          <Bouton taille="sm" type="button" title="Retirer ce contenant" onClick={() => { setMateriels(materiels.filter((_, k) => k !== i)); setMessage(''); }}>Retirer</Bouton>
        </div>

        {!m.accepte ? (
          <label style={etiquette}>Pourquoi il est refusé pour cet examen
            <input value={m.exception || ''} onChange={(e) => poser(i, { exception: e.currentTarget.value })} style={{ ...inputStyle, fontSize: 12.5 }} />
          </label>
        ) : (
          <>
            <div style={sousTitre}>Contenant et transport</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 8 }}>
              <label style={etiquette}>Contenant
                <select value={texte(m.recipient !== undefined ? m.recipient : p?.recipient)} onChange={(e) => poser(i, { recipient: e.currentTarget.value || null })} style={{ ...inputStyle, fontSize: 12.5 }}>
                  <option value="">— sans contenant propre —</option>
                  {enums.recipient.map((r) => <option key={r.code} value={r.code}>{r.libelle}</option>)}
                </select>
              </label>
              <label style={etiquette}>Mode de transport
                <select value={mode?.code || ''} onChange={(e) => poser(i, { transportMode: e.currentTarget.value as Entree['transportMode'] })} style={{ ...inputStyle, fontSize: 12.5 }}>
                  {MODES.map((x) => <option key={x.code} value={x.code}>{x.libelle}</option>)}
                </select>
              </label>
              <label style={etiquette}>Délai de transport maximal
                <input value={texte(m.transportDelaiMax !== undefined ? m.transportDelaiMax : p?.transportDelaiMax)} placeholder="ex. 4 h" onChange={(e) => poser(i, { transportDelaiMax: e.currentTarget.value })} style={{ ...inputStyle, fontSize: 12.5 }} />
              </label>
            </div>
            <label style={etiquette}>Consigne de transport
              <textarea rows={2} value={texte(m.transportConsigne !== undefined ? m.transportConsigne : p?.transportConsigne)} onChange={(e) => poser(i, { transportConsigne: e.currentTarget.value })} style={{ ...inputStyle, fontSize: 12.5, resize: 'vertical' }} />
            </label>

            <div style={sousTitre}>Conservation et volume</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 8 }}>
              {mode && (
                <label style={etiquette}>{mode.duree}
                  <input value={texte(m[mode.cle])} placeholder="ex. 24 h" onChange={(e) => poser(i, { [mode.cle]: e.currentTarget.value } as Partial<Entree>)} style={{ ...inputStyle, fontSize: 12.5 }} />
                </label>
              )}
              <label style={etiquette}>Volume minimal
                <input value={m.volumeMin || ''} placeholder="ex. 2 mL" onChange={(e) => poser(i, { volumeMin: e.currentTarget.value })} style={{ ...inputStyle, fontSize: 12.5 }} />
              </label>
            </div>
            <label style={etiquette}>Autre consigne propre à cet examen
              <textarea rows={2} value={m.consigne || ''} onChange={(e) => poser(i, { consigne: e.currentTarget.value })} style={{ ...inputStyle, fontSize: 12.5, resize: 'vertical' }} />
            </label>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 12px', fontSize: 12 }}>
              <span style={{ fontWeight: 700, color: 'var(--text-muted)' }}>Vu au catalogue par :</span>
              {LECTEURS_CATALOGUE.map((l) => (
                <label key={l.code} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <input type="checkbox" checked={lecteurs.includes(l.code)}
                    onChange={() => poser(i, { lecteurs: (lecteurs.includes(l.code) ? lecteurs.filter((x) => x !== l.code) : [...lecteurs, l.code]) as LecteurCatalogue[] })} />
                  {l.court}
                </label>
              ))}
            </div>
            {p && (!!p.criteresReception?.length || !!p.refuses?.length || !!p.attestations?.length) && (
              <details style={{ fontSize: 12.5 }}>
                <summary style={{ cursor: 'pointer', color: 'var(--text-link)', fontWeight: 600 }}>Critères de réception et refus (communs à « {p.libelle} », réglés dans Paramétrage › Profils de matériel)</summary>
                <div style={{ display: 'grid', gap: 4, marginTop: 6, color: 'var(--text-body)' }}>
                  {!!p.criteresReception?.length && <div><strong>Critères :</strong> {p.criteresReception.map((c) => c.libelle).join(' · ')}</div>}
                  {!!p.attestations?.length && <div><strong>À transmettre :</strong> {p.attestations.join(' · ')}</div>}
                  {!!p.resteAFaire?.length && <div><strong>Reste à faire :</strong> {p.resteAFaire.join(' → ')}</div>}
                  {!!p.refuses?.length && <div style={{ color: 'var(--red-600)' }}><strong>Refusé :</strong> {p.refuses.join(' · ')}</div>}
                </div>
              </details>
            )}
          </>
        )}
      </div>
    );
  };

  const lignesChoisi = choisi ? duMilieu(choisi) : [];
  const matieresChoisi = choisi ? (MATIERES_DU_MILIEU[choisi] || []) : [];
  const dejaSurChoisi = new Set(lignesChoisi.map(({ m }) => m.profil));
  const proposes = profils.filter((p) => !dejaSurChoisi.has(p.code));
  const acceptesChoisi = lignesChoisi.filter(({ m }) => m.accepte);
  const prepsChoisi = [...new Set(acceptesChoisi.map(({ m }) => (m.prep || '').trim()))];
  const prepCommune = prepsChoisi.length === 1 ? prepsChoisi[0] : '';
  const prepChoisies = prepCommune ? prepCommune.split(SEP).map((x) => x.trim()).filter(Boolean) : [];
  const optionsPrep = [...new Set([...preps, ...prepChoisies])].map((x) => ({ documentId: x, code: x, nom: x }));
  const libelleEtat = (p: Profil) => `${libelleContenant(p.recipient)} — ${p.matiere}, ${ETAT[p.etat] || p.etat}`;

  return (
    <div style={{ display: 'grid', gap: 16, margin: '14px 0 18px', minWidth: 0 }}>
      {confirmation}

      {/* ── Ce qui attend d'être publié ────────────────────────────────────── */}
      {ecarts.length > 0 && (
        <div role="status" style={{ ...bloc, background: 'var(--amber-50)', borderColor: 'var(--amber-100)', display: 'grid', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 13, color: 'var(--amber-700)' }}>
              {ecarts.length} modification{ecarts.length > 1 ? 's' : ''} non publiée{ecarts.length > 1 ? 's' : ''}
              {modifieLocal ? ' (dont certaines pas encore enregistrées)' : ' — au brouillon'}
            </strong>
            <span style={{ flex: 1 }} />
            <a href={`${cheminDe('manuel')}?onglet=circuit`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-link)' }}>
              Publier : Référentiel › Circuit ↗
            </a>
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, lineHeight: 1.5, color: 'var(--text-body)' }}>
            {ecarts.map((e) => <li key={e}>{e}</li>)}
          </ul>
          <div style={aide}>Le catalogue montre toujours la version publiée. Publier = <strong>Proposer</strong>, puis <strong>Valider</strong> (par un autre RQ que l’auteur), puis <strong>Diffuser</strong>.</div>
        </div>
      )}

      <fieldset disabled={!estRq || envoi} style={{ border: 'none', margin: 0, padding: 0, minWidth: 0, display: 'grid', gap: 16 }}>
        {/* ── 1. Milieux prélevés ──────────────────────────────────────────── */}
        <section>
          <div style={titreEtage}><span style={numero}>1</span>Ce qu’on prélève <span style={aide}>— cliquez un milieu pour le régler</span></div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
            {milieux.map((c) => {
              const actif = choisi === c;
              const n = duMilieu(c).filter(({ m }) => m.accepte).length;
              const ajoute = milieuxAjoutes.includes(c);
              return (
                <span key={c} style={{ display: 'inline-flex', alignItems: 'center', borderRadius: 'var(--radius-pill)', border: `1.5px solid ${actif ? 'var(--color-primary)' : ajoute ? 'var(--amber-100)' : 'var(--border-default)'}`, background: actif ? 'var(--color-primary)' : ajoute ? 'var(--amber-50)' : 'var(--surface-card)', color: actif ? 'var(--surface-card)' : 'var(--text-strong)' }}>
                  <button type="button" onClick={() => setChoisi(c)} style={{ background: 'none', border: 'none', color: 'inherit', font: 'inherit', fontSize: 13, fontWeight: 700, padding: '5px 4px 5px 12px', cursor: 'pointer' }}>
                    {libelleMilieu(c)} <span style={{ fontWeight: 500, opacity: 0.8 }}>· {n} contenant{n > 1 ? 's' : ''}{ajoute ? ' · non publié' : ''}</span>
                  </button>
                  {estRq && (
                    <button type="button" title={`Retirer ${libelleMilieu(c)}`} aria-label={`Retirer ${libelleMilieu(c)}`} onClick={() => void retirerMilieu(c)}
                      style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 15, padding: '2px 10px 2px 4px', opacity: 0.75 }}>×</button>
                  )}
                </span>
              );
            })}
            {milieuxRetires.map((c) => (
              <span key={`retire-${c}`} title="Retiré — pas encore publié" style={{ borderRadius: 'var(--radius-pill)', border: '1.5px dashed #EBD9B4', padding: '5px 12px', fontSize: 13, color: 'var(--amber-700)', textDecoration: 'line-through' }}>
                {libelleMilieu(c)}
              </span>
            ))}
            {aClasser.length > 0 && (
              <button type="button" onClick={() => setChoisi(A_CLASSER)}
                style={{ borderRadius: 'var(--radius-pill)', border: '1.5px dashed var(--amber-600)', background: choisi === A_CLASSER ? 'var(--amber-50)' : 'transparent', color: 'var(--amber-700)', fontSize: 13, fontWeight: 700, padding: '5px 12px', cursor: 'pointer' }}>
                À classer · {aClasser.length}
              </button>
            )}
            {!milieux.length && <span style={aide}>Aucun milieu déclaré.</span>}
            {estRq && (
              <span style={{ display: 'inline-flex', gap: 4 }}>
                <select value={ajoutMilieu} aria-label="Ajouter un milieu" onChange={(e) => setAjoutMilieu(e.currentTarget.value)} style={{ ...inputStyle, fontSize: 12.5, width: 'auto' }}>
                  <option value="">+ Ajouter un milieu…</option>
                  {enums.milieux.filter((m) => !milieux.includes(m.code)).map((m) => <option key={m.code} value={m.code}>{m.libelle}</option>)}
                </select>
                <Bouton taille="sm" type="button" disabled={!ajoutMilieu} onClick={ajouterMilieu}>Ajouter</Bouton>
              </span>
            )}
          </div>
        </section>

        {/* ── 2. Le milieu choisi ──────────────────────────────────────────── */}
        {choisi && (
          <section style={{ ...bloc, background: 'var(--surface-sunken)', display: 'grid', gap: 10 }}>
            <div style={titreEtage}>
              <span style={numero}>2</span>{libelleMilieu(choisi)}
              <span style={aide}>{choisi === A_CLASSER ? '— contenants sans milieu : rattachez-les' : enums.milieux.find((m) => m.code === choisi)?.aide || ''}</span>
            </div>

            {choisi !== A_CLASSER && (
              <div style={{ display: 'grid', gap: 4 }}>
                <div style={sousTitre}>Préparation du patient pour ce milieu</div>
                {!acceptesChoisi.length ? <div style={aide}>Ajoutez d’abord un contenant accepté.</div> : (
                  <>
                    {prepsChoisi.length > 1 && <div style={{ ...aide, color: 'var(--amber-700)' }}>Différente selon le contenant — ce que vous choisissez ici s’applique à tous.</div>}
                    <SelecteurPastilles options={optionsPrep} choisis={prepChoisies} mono={false} modifiable={estRq}
                      placeholder="Choisir une préparation…" vide="Aucune préparation choisie" ariaLabel="Préparation du patient" unite="préparation"
                      onChange={(ids) => { const v = ids.join(SEP); setMateriels(materiels.map((m) => (m.milieu === choisi && m.accepte ? { ...m, prep: v, source: 'declare' } : m))); setMessage(''); }} />
                    <div style={aide}>La liste se complète dans Paramétrage › Listes › « prep ».</div>
                  </>
                )}
              </div>
            )}

            <div style={sousTitre}>Contenants acceptés à réception</div>
            {lignesChoisi.length ? lignesChoisi.map(carte) : <div style={aide}>Aucun contenant pour ce milieu.</div>}

            {estRq && choisi !== A_CLASSER && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <select value={ajoutContenant} aria-label="Ajouter un contenant" onChange={(e) => setAjoutContenant(e.currentTarget.value)} style={{ ...inputStyle, fontSize: 12.5, flex: '1 1 260px', minWidth: 0 }}>
                  <option value="">+ Ajouter un contenant accepté à réception…</option>
                  {matieresChoisi.length > 0 && (
                    <optgroup label={`Issus de « ${libelleMilieu(choisi)} »`}>
                      {proposes.filter((p) => matieresChoisi.includes(p.matiere)).map((p) => <option key={p.code} value={p.code}>{libelleEtat(p)}</option>)}
                    </optgroup>
                  )}
                  <optgroup label="Autres (dérivés : ADN, isolat…)">
                    {proposes.filter((p) => !matieresChoisi.includes(p.matiere)).map((p) => <option key={p.code} value={p.code}>{libelleEtat(p)}</option>)}
                  </optgroup>
                </select>
                <Bouton taille="sm" type="button" disabled={!ajoutContenant} onClick={ajouter}>Ajouter</Bouton>
              </div>
            )}
          </section>
        )}

        {/* ── 3. Commun ────────────────────────────────────────────────────── */}
        <section>
          <div style={titreEtage}><span style={numero}>3</span>Commun à tous les contenants</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10 }}>
            <label style={etiquette}>Renseignements cliniques à fournir {renseignements !== publie.renseignements && <span style={nonPublie}>non publié</span>}
              <textarea rows={2} value={renseignements} onChange={(e) => { setRenseignements(e.currentTarget.value); setMessage(''); }} style={{ ...inputStyle, fontSize: 12.5, resize: 'vertical' }} />
            </label>
            <label style={etiquette}>Délai d’ajout d’analyse {delaiAjout !== publie.delaiAjout && <span style={nonPublie}>non publié</span>}
              <input value={delaiAjout} placeholder="ex. 24 h" onChange={(e) => { setDelaiAjout(e.currentTarget.value); setMessage(''); }} style={{ ...inputStyle, fontSize: 12.5 }} />
            </label>
          </div>
          <div style={{ ...aide, marginTop: 6 }}>Les règles communes à tous les examens (identification…) se règlent dans Paramétrage › Lecture du catalogue.</div>
        </section>
      </fieldset>

      {estRq && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', position: 'sticky', bottom: 0, background: 'var(--surface-card)', padding: '10px 0', borderTop: '1px solid var(--border-default)' }}>
          <span style={{ flex: 1, fontSize: 12.5, color: erreur ? 'var(--red-600)' : message ? 'var(--green-600)' : 'var(--text-muted)' }}>
            {erreur || message || (modifieLocal ? 'Modifications non enregistrées.' : 'Rien à enregistrer.')}
          </span>
          {modifieLocal && (
            <Bouton taille="sm" type="button" onClick={() => { setMilieux(base.milieux); setMateriels(base.materiels); setRenseignements(base.renseignements); setDelaiAjout(base.delaiAjout); setMessage(''); }}>Annuler</Bouton>
          )}
          <Bouton variante="primaire" type="button" disabled={!modifieLocal || envoi} onClick={() => void enregistrer()}>
            {envoi ? 'Enregistrement…' : 'Enregistrer au brouillon'}
          </Bouton>
        </div>
      )}
    </div>
  );
}
